import React from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import {
  mapFSMCTotalToFatigueLevel,
  mapFSMCPhysicalToFatigueLevel,
  mapFSMCMentalToFatigueLevel
} from 'neuro-calculation-commons';

const fatigueLevelLocalizationsFI = {
  none: 'Kyselyn perusteella et koe uupumuksen tunnetta',
  mild: 'Kyselyn perusteella koet lievää uupumuksen tunnetta',
  moderate: 'Kyselyn perusteella koet kohtalaista uupumuksen tunnetta',
  severe: 'Kyselyn perusteella koet huomattavaa uupumuksen tunnetta'
};

const physicalFatigueLevelLocalizationsFI = {
  none: 'Kyselyn perusteella et koe fyysisen uupumuksen tunnetta',
  mild: 'Kyselyn perusteella koet lievää fyysisen uupumuksen tunnetta',
  moderate: 'Kyselyn perusteella koet kohtalaista fyysisen uupumuksen tunnetta',
  severe: 'Kyselyn perusteella koet huomattavaa fyysisen uupumuksen tunnetta'
};

const cognitiveFatigueLevelLocalizationsFI = {
  none: 'Kyselyn perusteella et koe kognitiivisen uupumuksen tunnetta',
  mild: 'Kyselyn perusteella koet lievää kognitiivisen uupumuksen tunnetta',
  moderate: 'Kyselyn perusteella koet kohtalaista kognitiivisen uupumuksen tunnetta',
  severe: 'Kyselyn perusteella koet huomattavaa kognitiivisen uupumuksen tunnetta'
};

const InfoFI = ({ totalScore, statusMood, document }) => (
  <React.Fragment>
    <div className={mainHeader}>Yhteenveto kokemastasi uupumuksesta</div>

    <div className={subjectArea}>
      <div className={subHeader}>Uupumuksen tunne</div>
      <ul>
        <li>
          Sait kyselystä <span style={{ fontWeight: 600 }}>{totalScore}</span> pistettä (pienin mahdollinen pistemäärä
          20, suurin mahdollinen 100).
        </li>
        {<li>{fatigueLevelLocalizationsFI[mapFSMCTotalToFatigueLevel(totalScore)]}</li>}
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Fyysisen uupumuksen tunne</div>
      <ul>
        <li>
          Sait fyysistä uupumusta kartoittavasta osiosta{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'physical'], document)}</span> pistettä (pienin mahdollinen
          pistemäärä 10, suurin mahdollinen 50)
        </li>
        {
          <li>
            {physicalFatigueLevelLocalizationsFI[mapFSMCPhysicalToFatigueLevel(path(['scores', 'physical'], document))]}
          </li>
        }
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Kognitiivisen uupumuksen tunne (=ajattelutoimintojen uupuminen)</div>
      <ul>
        <li>
          Sait kognitiivista uupumusta kartoittavasta osiosta{' '}
          <span style={{ fontWeight: 600 }}>{path(['scores', 'mental'], document)}</span> pistettä (pienin mahdollinen
          pistemäärä 10, suurin mahdollinen 50)
        </li>
        {
          <li>
            {cognitiveFatigueLevelLocalizationsFI[mapFSMCMentalToFatigueLevel(path(['scores', 'mental'], document))]}
          </li>
        }
      </ul>
    </div>

    {statusMood > 0 && (
      <div className={subjectArea}>
        <span>
          On hyvä huomata, että kokemasi uupumus saattaa ainakin osittain selittyä kokemallasi mielialan
          alavireisyydellä. Otathan mahdollisen mielialan alavireisyyden puheeksi MS-hoitajan tai lääkärisi kanssa.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Tietoa uupumuksesta</div>
      Uupumus tuntuu usein varsin vaikeasti hallittavalta oireelta. Toisinaan tehtävät ja velvollisuudet hoituvat
      suunnitellusti, toisinaan yllättävä uupumus tuo mutkia matkaan. Uupumuksen kanssa pärjää paremmin, kun tietää,
      milloin se tyypillisesti ilmenee, mitkä tekijät voimistavat sitä ja mitkä helpottavat ja miten se vaikuttaa
      toimintakykyyn. Kyselyn avulla sait tietoa siitä, ilmeneekö uupumuksen tunteesi enemmän fyysisenä vai
      kognitiivisena uupumuksena. Toimintapäiväkirjan pitäminen on hyvä keino tunnistaa uupumuksen tunteeseen
      vaikuttavia tekijöitä. Kirjaa esimerkiksi viikon ajan seuraavat asiat päiväkirjaan: miten nukuit, milloin ja mitä
      söit, päivittäiset fyysiset aktiviteetit, päivittäiset työtehtävät ja sosiaaliset kontaktit. Kirjaa päiväsaikaan
      esimerkiksi kahden tunnin välein kokemasi uupumuksen aste (0 = ei lainkaan uupunut – 10 = erittäin uupunut).
      Päiväkirjamerkintöjä tutkimalla huomaat helposti, onko unella, ravitsemuksella ja toiminnoillasi merkitystä
      kokemaasi uupumukseen. Voit löytää tekijöitä, jotka uuvuttavat ja myös tekijöitä, jotka ylläpitävät jaksamisen
      tunnetta.
      <br />
      <br />
      Uupumuksen kanssa pärjäämistä voi helpottaa kiinnittämällä huomiota omiin toimintatapoihin ja ympärillä oleviin
      tekijöihin. Seuraavista vinkeistä voi olla sinulle apua:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Apua arkeen</div>
      Uni
      <br />
      <ul>
        <li>kiinnitä huomiota unihygieniaan</li>
        <ul>
          <li>pyri rauhoittamaan hetki ennen nukkumaan menoa</li>
          <li>jos mielessäsi pyörii erilaisia huolehdittavia asioita, listaa ne paperille ennen nukkumaan menoa</li>
          <li>karsi ylimääräiset häiriötekijät</li>
          <li>
            jos esimerkiksi kivut tai virtsanpidätysvaikeudet häiritsevät yöuntasi, ota ne puheeksi lääkärisi kanssa
          </li>
        </ul>
      </ul>
      <br />
      Ravitsemus ja nestetasapaino
      <ul>
        <li>huomioi ravitsemussuositukset</li>
        <ul>
          <li>
            väsymyksen tunteen taustalla saattaa olla energiavaje – kun sairauteesi liittyy uupumusta, oikean
            ravitsemuksen merkitys korostuu
          </li>
          <li>pyri syömään aamiainen, lounas, välipala, päivällinen ja iltapala</li>
          <li>kiinnitä huomiota, etteivät ateriavälit veny liian pitkiksi</li>
          <li>ateriakoon pienentäminen ja kasvisten määrän lisääminen saattaa tukea jaksamista</li>
          <li>muista juoda riittävästi vettä pitkin päivää</li>
        </ul>
      </ul>
      <br />
      Fyysinen ja kognitiivinen aktiivisuus ja energian säästäminen
      <ul>
        <li>pidä huolta fyysisestä toimintakyvystäsi</li>
        <ul>
          <li>harrasta sellaista liikuntaa, joka sopii sinulle parhaiten eikä uuvuta liikaa</li>
          <li>sopiva liikuntaa parantaa jaksamista ja vähentää usein uupumuksen tunnetta</li>
        </ul>
        <li>pidä huolta kognitiivisesta toimintakyvystäsi</li>
        <ul>
          <li>
            mielekkäiden asioiden tekeminen vahvistaa hermoverkon toimintaa, suojaa kognitiivisilta ongelmilta ja tukee
            jaksamista
          </li>
        </ul>
        <li>kiinnitä huomiota, milloin, miten ja mitä teet</li>
        <ul>
          <li>pidä yllä tehtävälistaa</li>
          <li>tee tärkeimmät asiat, kun olet pirteimmilläsi</li>
          <li>vältä kiirettä ja tilanteita, joissa sinulla on monta asiaa kesken yhtä aikaa</li>
          <li>varaa itsellesi riittävästi aikaa</li>
          <li>käytä toimintatapoja, jotka säästävät energiaa</li>
          <ul>
            <li>käytä apuvälineitä</li>
            <li>pyri karsimaan häiriötekijät</li>
            <li>tarkkaile jaksamistasi, tauota ja lepää tarvittaessa</li>
          </ul>
        </ul>
      </ul>
      <br />
      Ympäristötekijät
      <ul>
        <li>jos toimintasi on häiriöaltista, pyri karsimaan häiriötekijät, kuten ylimääräiset ääni- ja valonlähteet</li>
        <li>jos lämpö pahentaa oireitasi ja aiheuttaa uupumuksen tunnetta</li>
        <ul>
          <li>vältä saunomista</li>
          <li>laske huonelämpötilaa</li>
          <li>käytä viilennyskeinoja, kuten kylmäpakkaukset, viilennetyt vaatteet, kylmä suihku, tuuletus jne.</li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>Miten tästä eteenpäin</div>
      On tärkeää, että pysyt aktiivisena uupumuksesta huolimatta. Jos uupumus aiheuttaa haasteita arjessa
      selviytymiseen, esimerkiksi opiskeluissa etenemiseen tai työssä selviytymiseen, ota asia puheeksi MS-hoitajan
      ja/tai lääkärisi kanssa. Yhdessä voitte arvioida jatkotoimenpiteiden tarvetta. Uupumuksen kanssa selviytymistä on
      mahdollista tukea ohjauksen ja kuntoutuksen avulla. Löydät lisätietoa kuntoutusmahdollisuuksista esimerkiksi Kelan
      sivuilta{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (MS-kuntoutuskurssit, harkinnanvarainen ja vaativa yksilöllinen moniammatillinen kuntoutus neurologisella
      linjalla, neuropsykologinen kuntoutus, toimintaterapia, fysioterapia) ja Neuroliitto ry:n sivuilta{' '}
      <a href="https://www.kuntoutukseen.fi" target="blank">
        www.kuntoutukseen.fi
      </a>
      . Oireista on hyvä kertoa myös läheisillesi. Voit käyttää apuna tätä kyselystä saamaasi yhteenvetoa sekä
      Neuroliitto ry:n sivuilta löytyvää Uupumus MS-taudissa -opasta (
      <a href="https://neuroliitto.fi/tuote/uupumus-ms-taudissa/" target="blank">
        Uupumus MS-taudissa
      </a>
      ). Oppaasta löytyy myös toimintapäiväkirjamalli.
    </div>
  </React.Fragment>
);

InfoFI.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  document: PropTypes.object
};

export default InfoFI;
